'use client'

import { Dispatch, SetStateAction, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { clsx } from 'clsx'
import { useSetAtom } from 'jotai'
import { useParams } from 'next/navigation'

import { StatusSuccessIcon } from '@dfds-frontend/navigator-icons/icons'
import { ButtonClick, track } from '@dfds-frontend/tracking'
import { BffInterfacesBasket } from '@dfds-pax/booking-experience-bff'

import getSeabookResource from '@/app/[locale]/[ferry]/_actions/getSeabookResource'
import Price from '@/components/shared/Price'
import { selectedOfferAtom } from '@/states/selectionAtom'
import CloseIcon from '@/ui-cvi/components/dxp-icons/system/CloseIcon'
import EditIcon from '@/ui-cvi/components/dxp-icons/system/EditIcon'

import { extraShortDateFormat } from '@/utils/dateAndTimeHelpers'

import { useWindowWidth } from '@/hooks/useWindowWidth'

import { Step } from '../steps'
import { SummaryContent } from './SummaryContent'

interface Props {
  basket: BffInterfacesBasket.Basket
  currentIndex: number
  currentStep: Step
  setShowSummary: Dispatch<SetStateAction<boolean>>
  showSummary: boolean
  steps: Step[]
}

const hideOffer = (departures: BffInterfacesBasket.Departure[]): boolean => {
  return departures.filter((d) => d.offer && d.offer.hidePriceAdjustment).length > 0
}

export const StickySummaryContainer: React.FC<Props> = ({ basket, currentIndex, setShowSummary, showSummary, steps }) => {
  const setSelectedOffer = useSetAtom(selectedOfferAtom)
  const { widthSmallerThan768 } = useWindowWidth()
  const { t } = useTranslation()
  const { locale } = useParams()

  // Compute offer directly
  const offer = basket.departures.find((d) => d.offer)?.offer
  // Update atom if offer changes
  useEffect(() => {
    if (!offer) return
    let active = true
    const applyOffer = async () => {
      const offerTitle = offer.code ? await getSeabookResource(locale as string, offer.code, 'offer') : undefined
      if (!active) return
      // Functional update merges against the latest state so a concurrent offer change isn't clobbered.
      setSelectedOffer((prev) => ({
        ...prev,
        offerCode: offer.code,
        offerDescription: offer.description,
        offerTitle,
      }))
    }
    applyOffer()
    return () => {
      active = false
    }
    // eslint-disable-next-line
  }, [offer])

  // Compute hidePriceAdjustment directly
  const hidePriceAdjustment = basket.departures.some((d) => d.offer && d.offer.hidePriceAdjustment)
  const totalPrice = basket.priceStructure?.totalPrice || 0
  const totalOfferPrice = basket.priceStructure?.offerAmount < 0 && !hidePriceAdjustment ? basket.priceStructure?.totalPriceWithoutOffer : 0
  const currency = basket.priceStructure?.currency

  // Lock scroll when summary is open
  useEffect(() => {
    document.body.style.overflow = showSummary ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
      document.body.style.height = '100px'
    }
  }, [showSummary])

  // Close summary on width change
  useEffect(() => {
    setShowSummary(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widthSmallerThan768])

  const toggleSummary = () => {
    if (!showSummary) {
      scrollTo(0, 0)
      document.documentElement.classList.add('locked')
      document.getElementById('SummaryPortal')?.scrollTo(0, 0)
    } else {
      document.documentElement.classList.remove('locked')
    }
    setShowSummary(!showSummary)
  }

  // Dummy departures have no id, so the discount only applies once a real sailing is picked.
  const hasResidentVehicleSubsidy = basket?.commercialDiscount === 'Resident' && basket.departures.some((d) => d.id)

  return (
    <div className={clsx('relative flex w-full flex-col lg:h-[calc(100dvh-62px)]', showSummary && 'h-[calc(100dvh-174px)]')}>
      <div
        className="bg-semantic-global-background-white p-primitive-16 border-semantic-global-border-light z-[100] flex h-[44px] w-full flex-row items-center justify-between border-b"
        data-e2e="stepper-sticky"
        onClick={() => {
          track<ButtonClick>({
            clickedElementText: showSummary ? `closed` : 'opened',
            event: 'buttonClick',
            eventAction: 'ViewSummaryClicked',
          })
          toggleSummary()
        }}
      >
        <div className="typography-body-md-regular">
          {basket.departures[0] ? extraShortDateFormat(basket.departures[0]?.departureDate) : null}
          {basket.departures[1] ? ` - ${extraShortDateFormat(basket.departures[1]?.departureDate)}` : null}
        </div>
        <div className="text-semantic-global-text-default flex flex-row items-center">
          <div className={clsx('pl-primitive-8 gap-primitive-8 flex h-[20px] flex-row items-center justify-center')}>
            {!showSummary ? (
              <div className="gap-primitive-8 text-semantic-global-primary-default pl-primitive-12 border-semantic-global-border-light flex flex-row items-center border-l border-solid">
                <span className="typography-body-md-medium truncate">{t('ITINERARY')}</span> <EditIcon size="md" />
              </div>
            ) : null}
            {showSummary ? (
              <div className="px-primitive-8 text-semantic-global-primary-default items-center">
                <CloseIcon size="lg" />
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div
        className={clsx(
          !showSummary && 'hidden',
          `bg-semantic-global-background-light-grey border-semantic-global-border-light pt-primitive-24 px-semantic-global-md h-full overflow-y-auto border-t lg:pt-0`
        )}
      >
        <SummaryContent
          basket={basket}
          currency={currency}
          currentIndex={currentIndex}
          hideOffer={hideOffer}
          offer={offer}
          steps={steps}
          t={t}
          toggleSummary={toggleSummary}
          totalOfferPrice={totalOfferPrice}
          totalPrice={totalPrice}
        />
      </div>
      <section
        className={clsx(
          'p-semantic-global-md border-semantic-global-border-default bg-semantic-global-background-white w-full border-t',
          !showSummary && 'hidden'
        )}
      >
        <div className="flex flex-col gap-2.5">
          <div className="flex flex-row items-center">
            <div className="typography-heading-sm">{t('TOTAL-PRICE')}</div>
            <div className="ml-auto">
              <div
                className="typography-number-md mobile:ml-auto md:ml-primitive-8 text-dfds-text-primary-primary"
                data-e2e="booking-summary-total-price"
                data-e2e-total-price={totalPrice}
                data-e2e-total-price-mobile={totalPrice}
              >
                <Price currency={currency} price={totalPrice} />
              </div>
            </div>
          </div>
          {hasResidentVehicleSubsidy ? (
            <div className="text-semantic-global-success-default gap-semantic-global-xs flex flex-row items-center">
              <div className="typography-body-md-regular min-w-0 flex-1 truncate">{t('RESIDENT-VEHICLE-DISCOUNT-APPLIED')}</div>
              <div className="ml-auto">
                <StatusSuccessIcon size="md" />
              </div>
            </div>
          ) : null}
        </div>

        <button
          className="mt-semantic-global-md button-light-secondary-lg w-full"
          onClick={() => {
            toggleSummary()
          }}
          type="button"
        >
          {t('CLOSE')}
        </button>
      </section>
    </div>
  )
}
